/**
 * Internal module for initializing Snowplow and making Snowplow-specific event calls. Public init functions can be found in Initializer module
 * @module Gtm
 */
import * as Sentry from "@sentry/react";

import { AnalyticsState } from "./analytics-state";
import { AnalyticsData } from "./gasv3";
export class Gtm {
    /**
     * Initializes Google Tag Manager
     * @function initialize
     */
    public static initialize(writeKey: string) {
        // Add Sentry breadcrumb for debugging script loading issues
        Sentry.addBreadcrumb({
            message: "GTM initialization started",
            data: {
                writeKey,
                pathname: window.location.pathname,
                onetrustStatus:
                    window.OnetrustActiveGroups !== undefined
                        ? "ready"
                        : "not ready",
                onetrustActiveGroups:
                    window.OnetrustActiveGroups !== undefined
                        ? window.OnetrustActiveGroups
                        : "window.OnetrustActiveGroups undefined",
                dataLayerExists: !!window["dataLayer"]
            },
            level: "info",
            category: "gtm"
        });

        // Polyfill localStorage to prevent third-party script errors
        if (!window.localStorage) {
            window.localStorage = {
                getItem: () => null,
                setItem: () => {
                    /* no-op */
                },
                removeItem: () => {
                    /* no-op */
                },
                clear: () => {
                    /* no-op */
                },
                length: 0,
                key: () => null
            };
        }

        const containerId = Gtm.getContainerId(writeKey);

        Sentry.addBreadcrumb({
            message: "GTM container ID resolved",
            data: { writeKey, containerId },
            level: "info",
            category: "gtm"
        });

        if (containerId) {
            window["dataLayer"] = window["dataLayer"] || [];
            window["dataLayer"].push({
                "gtm.start": new Date().getTime(),
                "event": "gtm.js"
            });

            Sentry.addBreadcrumb({
                message: "GTM dataLayer initialized",
                data: {
                    containerId,
                    dataLayerLength: window["dataLayer"].length
                },
                level: "info",
                category: "gtm"
            });

            const firstScript = document.getElementsByTagName("script")[0];
            const gtmScript = document.createElement("script");
            gtmScript.async = true;
            gtmScript.src =
                "https://www.googletagmanager.com/gtm.js?id=" + containerId;

            // Add error handling and monitoring for the GTM script
            gtmScript.onerror = (error) => {
                Sentry.addBreadcrumb({
                    message: "GTM script failed to load",
                    data: {
                        containerId,
                        src: gtmScript.src,
                        error: error.toString()
                    },
                    level: "error",
                    category: "gtm"
                });

                Sentry.captureException(
                    new Error(
                        `GTM script load failed: ${JSON.stringify(error)}`
                    ),
                    {
                        tags: {
                            component: "gtm-integration",
                            containerId,
                            pathname: window.location.pathname
                        }
                    }
                );
            };

            gtmScript.onload = () => {
                Sentry.addBreadcrumb({
                    message: "GTM script loaded successfully",
                    data: {
                        containerId,
                        src: gtmScript.src
                    },
                    level: "info",
                    category: "gtm"
                });
            };

            Sentry.addBreadcrumb({
                message: "Attempting to inject GTM script",
                data: {
                    containerId,
                    src: gtmScript.src,
                    hasFirstScript: !!firstScript,
                    parentNode: firstScript?.parentNode?.nodeName
                },
                level: "info",
                category: "gtm"
            });

            firstScript?.parentNode?.insertBefore(gtmScript, firstScript);
        } else {
            Sentry.addBreadcrumb({
                message: "GTM container ID not found",
                data: { writeKey },
                level: "warning",
                category: "gtm"
            });
        }
    }

    /**
     * Sends a gtm page call
     * @function page
     */
    public static page(
        name: string | null,
        category: string | null,
        data: AnalyticsData
    ) {
        let event = "Loaded a Page";

        Gtm.track(data, event);

        if (category) {
            event = "Viewed " + name + " " + category + " Page";
            Gtm.track(data, event);
        }

        if (name) {
            event = "Viewed " + name + " Page";
            Gtm.track(data, event);
        }
    }

    public static track(data: AnalyticsData, event: string) {
        try {
            const props =
                typeof data !== "undefined"
                    ? JSON.parse(JSON.stringify(data))
                    : {};
            const anonId = AnalyticsState.anonymousId;
            if (anonId) {
                props.gasv3AnonymousId = anonId;
            }

            if (event) {
                props.event = event;
            }

            const dataLayer = "dataLayer";
            window[dataLayer] = window[dataLayer] || [];

            Sentry.addBreadcrumb({
                message: "GTM track event",
                data: {
                    event,
                    dataLayerLength: window[dataLayer].length,
                    hasData: !!data,
                    pathname: window.location.pathname
                },
                level: "debug",
                category: "gtm"
            });

            window[dataLayer].push(props);
        } catch (error) {
            Sentry.addBreadcrumb({
                message: "GTM track failed",
                data: {
                    event,
                    error:
                        error instanceof Error ? error.message : String(error),
                    pathname: window.location.pathname
                },
                level: "error",
                category: "gtm"
            });

            Sentry.captureException(error, {
                tags: {
                    component: "gtm-track",
                    event,
                    pathname: window.location.pathname
                }
            });
        }
    }

    public static getContainerId(writeKey: string) {
        const containerIds = Gtm.getContainerIdMapping();
        return containerIds[writeKey];
    }

    public static getContainerIdMapping(): Record<string, string> {
        return {
            // Production
            "ycih1ygfsk": "GTM-MR6PJQR", // support.atlassian.com
            "9nfqb81km3": "GTM-TSGXFNC", // confluence.atlassian.com
            "kFTUHPhH9Q": "GTM-TRS3MV", // bitbucket
            "u7y09b0olq": "GTM-5VS67RK", // developer.atlassian.com
            "eNVsl3NOQquS5E0YXjD4prfiNVqVP3mr": "GTM-KZLJPX4", // opsgenie
            "bvsetm1g1y": "GTM-5RJSBR", // atlassian.com-cart
            "kiv6wyh2nw": "GTM-5RJSBR", // atlassian.com
            "r4n974s99s": "GTM-5RJSBR", // blogs.atlassian.com
            "swoogo": "GTM-5RJSBR", // events.atlassian.com
            "8oi64no": "GTM-K7MDT22", // statuspage.io
            "xjtlog1hqa": "GTM-KQ6R2C3", // marketplace.atlassian.com
            "bNSPpYAL9OiAkamBRvfRJoRu9YANW14I": "GTM-MGV77B", // docs.atlassian.com-aui
            "TNnhg0QuQfHEpBTzp27kJXNWZoMqSvkw": "GTM-TQZMFCL", // community.atlassian.com
            "trello": "GTM-PJ8M5SK", // trello

            // Test
            "4J6H7c3XTcpm1dbenkOILSyOachvf9It": "GTM-TQZMFCL", // community.stg.atlassian.com
            "SBLZG3ZYcSBcUMeLKLalrtyW8w1Caold": "GTM-TQZMFCL", // community.dev1.dev.atlassian.com
            "QVFu18jjHlCNaBXizZ3mtiqlBXVRkg1M": "GTM-TQZMFCL", // community.dev2.dev.atlassian.com
            "QmWM6iw7auEJw5gDQi6910Xk1wqyZ6bE": "GTM-TQZMFCL", // community.dev3.dev.atlassian.com
            "hWTszOAQMuItRdv8J30A3mNpCWd891bx": "GTM-TQZMFCL", // community.dev4.dev.atlassian.com
            "EWfeqJKJ0rPNOe3fcXrh2dg7bVXZNi2I": "GTM-5RJSBR", // qa-wac.internal.atlassian.com
            "Z2S07IGcRYkPJ7XHy3OIXcjpYoy9E6bs": "GTM-5RJSBR" // CXP Local Dev
        };
    }
}
